import React, { useCallback, useMemo } from 'react';
import { FormattedDate, FormattedMessage, FormattedTime } from 'react-intl';

import { useMessage } from '@tmapy/intl';
import { useDispatch, useSelector } from '@tmapy/redux';

import { SvgReload, SvgCross, TertiaryBtn, InfoBadge, Link } from '@tmapy/style-guide';

import type { OrderListResponse } from '../../openapi/types';
import { getCmdCtrlUrl } from '../../utils/getcmdctrlurl';
import { RouteId } from '../../routes';
import { useAppContext, useAuth, useHistoryAction } from '../../hooks';
import { orderRetryProcessing, orderInterruptProcessing } from '../actions/orderDetail';
import { usableItemsForFormType } from '../constants';
import { dmvsSourceMessages, orderMessages, originMessages, typesMessages } from './orderMessages';
import { OrderBadge } from './OrderBadge';

type OrderListItemProps = OrderListResponse & {
  refetchData(): void;
};

export const OrderListItem: React.FC<OrderListItemProps> = (props) => {
  const { id, refetchData } = props;
  const dispatch = useDispatch();
  const formatMessage = useMessage();
  const routeToOrderDetail = useHistoryAction('vydejOrderDetail', { id });

  const { appType } = useAppContext();
  const auth = useAuth();
  const { customerType } = useSelector((state) => state.app.config);
  const usableItems = usableItemsForFormType[customerType];
  const acl = useMemo(
    () => ({
      // TODO BEG - rename ACL to actions
      canInterruptProcessing: auth.hasPermission('Orders', 'interrupt_processing', props),
      canRetryProcessing: auth.hasPermission('Orders', 'retry_processing', props),
      ordersActionHiddenCreator: auth.hasPermission('Orders', 'action.hidden_creator', props),
    }),
    [auth, props],
  );

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      if (event.ctrlKey || event.metaKey) {
        window.open(
          getCmdCtrlUrl('vydejOrderDetail' as RouteId, { appType, id: props.id }),
          '_blank',
        );
        return; // Ponech nativní chování
      }

      const selectText = window.getSelection();
      if (!selectText || selectText.toString() === '') {
        routeToOrderDetail();
      }
    },
    [props.state, routeToOrderDetail],
  );

  const handleInterruptProcessingClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      const response = await dispatch<any>(orderInterruptProcessing(id));
      response.ok && refetchData();
    },
    [dispatch, id, refetchData],
  );

  const handleRetryProcessingClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      const response = await dispatch<any>(orderRetryProcessing(id));
      response.ok && refetchData();
    },
    [dispatch, id, refetchData],
  );

  const publishset_names = props.publishsets.map((publishset) => publishset.publishset_name);
  const required_formats = [
    ...new Set(props.publishsets.flatMap((publishset) => publishset.required_formats)),
  ];

  return (
    <tr onClick={handleClick}>
      <td className='sg-a-ta-c'>
        <div className='sg-u-type-bold sg-a-fvn-tn'>{id}</div>
        {usableItems?.includes('state') && (
          <div className='sg-a-mt-1/2'>
            <OrderBadge state={props.state} />
          </div>
        )}
      </td>
      {usableItems?.includes('selection_type') && (
        <td>{formatMessage.lookup(typesMessages, props.selection_type!)}</td>
      )}
      {usableItems?.includes('dmvs_id_podani') && <td>{props.dmvs_id_podani}</td>}
      {usableItems?.includes('dmvs_source') && (
        <>
          <td>
            {props.dmvs_source
              ? formatMessage.lookup(dmvsSourceMessages, props.dmvs_source)
              : `${formatMessage(orderMessages.dmvsSourceEmpty)} ${
                  props.from_primary_db ? formatMessage(orderMessages.fromPrimaryDB) : ''
                }`}
          </td>
        </>
      )}
      {usableItems?.includes('is_public') && (
        <td>
          <FormattedMessage
            {...orderMessages.isPublicValue}
            values={{ is_public: props.is_public }}
          />
        </td>
      )}
      {usableItems?.includes('origin') && !acl.ordersActionHiddenCreator && (
        <td>{props.origin && formatMessage.lookup(originMessages, props.origin)}</td>
      )}
      {usableItems?.includes('creator') && !acl.ordersActionHiddenCreator && (
        <td>
          <div>{`${props.originator_name} ${props.originator_surname}`.trim()}</div>
          {props.originator_email && (
            <div className='sg-u-type-sml sg-a-mt-1/2'>
              <Link href={`mailto:${props.originator_email}`} severity='colorful'>
                {props.originator_email}
              </Link>
            </div>
          )}
        </td>
      )}
      {usableItems?.includes('publishsets') && (
        <>
          <td>
            {publishset_names[0]}
            <span className='sg-u-type-xxs sg-u-c-body-lightest'>
              {publishset_names.length > 1 ? `\u00A0(+${publishset_names.length - 1})` : ''}
            </span>
          </td>
          <td>
            <div className='sg-a-d-f sg-a-fw-w'>
              {required_formats.map((format) => (
                <span className='sg-a-mt-1/2 sg-a-mr-1/2' key={format}>
                  <InfoBadge>{format}</InfoBadge>
                </span>
              ))}
            </div>
          </td>
        </>
      )}
      {usableItems?.includes('created') && (
        <td className='sg-a-ta-r sg-a-ws-nw sg-a-fvn-tn'>
          {props.created && (
            <>
              <FormattedDate value={new Date(props.created)} />{' '}
              <FormattedTime value={new Date(props.created)} />
            </>
          )}
        </td>
      )}
      {usableItems?.includes('published') && (
        <td className='sg-a-ta-r sg-a-ws-nw sg-a-fvn-tn'>
          {props.published && (
            <>
              <FormattedDate value={new Date(props.published)} />{' '}
              <FormattedTime value={new Date(props.published)} />
            </>
          )}
        </td>
      )}
      <td className='sg-a-w-0'>
        {acl.canRetryProcessing && (
          <TertiaryBtn
            icon={{ element: <SvgReload /> }}
            tooltip={formatMessage(orderMessages.reprocessBtn)}
            onClick={handleRetryProcessingClick}
          />
        )}
        {acl.canInterruptProcessing && (
          <TertiaryBtn
            isDanger
            icon={{ element: <SvgCross /> }}
            tooltip={formatMessage(orderMessages.interruptBtn)}
            onClick={handleInterruptProcessingClick}
          />
        )}
      </td>
    </tr>
  );
};
